// The dashboard's thesis (§5.2): a "viewer card" that says who you are, not how
// many rows you have. Critic type is the signature element — everything else
// stays quiet around it. Enriched signals (signature genre, contrarian meter)
// slot in once the TMDB sweep completes.

import type { DistilledProfile } from "../lib/types.ts";
import { computeTasteSignals } from "../lib/tasteSignals.ts";
import { ScoreDial } from "./Charts.tsx";

export function TasteSignature({
  profile,
  signatureGenre,
  contrarian,
}: {
  profile: DistilledProfile;
  /** Top genre, filled in by enrichment. */
  signatureGenre?: string | null;
  /** Mean distance from the crowd (0..5), filled in by enrichment. */
  contrarian?: number | null;
}) {
  const s = computeTasteSignals(profile);
  // Map avg rating (0.5–5) to a 0–100 dial.
  const dial = Math.round((s.avgRating / 5) * 100);

  return (
    <div data-testid="taste-signature" className="card overflow-hidden">
      <div className="flex items-center gap-5">
        <ScoreDial score={dial} size={96} />
        <div className="min-w-0">
          <p className="stat-label">Your rating personality</p>
          <h3 className="mt-0.5 text-2xl font-bold text-ink-900 sm:text-3xl">
            {s.criticType} <span className="text-gold-500">critic</span>
          </h3>
          <p className="mt-1 text-sm text-ink-400">{s.criticBlurb}</p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-paper-200 sm:grid-cols-4">
        <Gauge label="Home decade" value={s.definingDecade ?? "—"} sub={s.definingDecade ? `${Math.round(s.definingDecadeShare * 100)}% of watches` : "no data"} accent="teal" />
        <Gauge label="Eras spanned" value={s.erasSpanned ? `${s.erasSpanned}` : "—"} sub="decades of film" accent="cream" />
        <Gauge label="Backlog" value={s.backlog.toLocaleString()} sub="on the watchlist" accent="orange" />
        {signatureGenre !== undefined ? (
          <Gauge label="Signature genre" value={signatureGenre ?? "—"} sub="most-watched" accent="pink" text />
        ) : (
          <Gauge label="Rated" value={`${Math.round(s.ratedShare * 100)}%`} sub="of your library" accent="pink" />
        )}
      </div>

      {contrarian != null && (
        <ContrarianMeter value={contrarian} />
      )}
    </div>
  );
}

function Gauge({
  label,
  value,
  sub,
  accent,
  text,
}: {
  label: string;
  value: string;
  sub: string;
  accent: "orange" | "teal" | "pink" | "cream";
  /** Text values (e.g. a genre name) size down and wrap; numbers stay large. */
  text?: boolean;
}) {
  const color =
    accent === "orange" ? "text-gold-500" : accent === "teal" ? "text-green-500" : accent === "pink" ? "text-red-600" : "text-ink-900";
  return (
    <div className="bg-card p-3">
      <div
        className={`font-semibold leading-tight ${color} ${
          text ? "text-base" : "text-xl tabular-nums"
        }`}
      >
        {value}
      </div>
      <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-ink-900/80">{label}</div>
      <div className="text-[11px] text-ink-400">{sub}</div>
    </div>
  );
}

// How far the viewer sits from consensus, on a 0 (with the crowd) → 2.5+
// (contrarian) scale. Framed playfully.
function ContrarianMeter({ value }: { value: number }) {
  const pct = Math.min(100, (value / 2.5) * 100);
  const label = value >= 1.5 ? "Contrarian" : value >= 0.8 ? "Independent" : "In step with the crowd";
  return (
    <div className="mt-4" data-testid="contrarian-meter">
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-sm font-medium text-ink-900">{label}</span>
        <span className="text-xs text-ink-400">±{value.toFixed(2)}★ from the world</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-paper-100">
        <div
          className="h-full rounded-full bg-gradient-to-r from-green-500 via-gold-500 to-red-600"
          style={{ width: `${Math.max(6, pct)}%` }}
        />
      </div>
    </div>
  );
}
